import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { 
  Bold, Italic, Underline, List, ListOrdered, Link as LinkIcon, 
  Image as ImageIcon, AlignLeft, AlignCenter, AlignRight, AlignJustify, Code,
  Heading1, Heading2, Heading3, Quote, Undo, Redo, Strikethrough, 
  Subscript, Superscript, RemoveFormatting, Type, Palette, Eye, Code2
} from 'lucide-react';
import { Card } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
}

export function RichTextEditor({ 
  value, 
  onChange, 
  placeholder = 'Start typing...', 
  minHeight = '400px' 
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [showHtmlMode, setShowHtmlMode] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showBgColorPicker, setShowBgColorPicker] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#000000');
  const [selectedBgColor, setSelectedBgColor] = useState('#ffffff');
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [imageWidth, setImageWidth] = useState('');
  const [imageHeight, setImageHeight] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update editor content when value changes externally
  useEffect(() => {
    if (editorRef.current && !showHtmlMode && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value, showHtmlMode]);

  const execCommand = (command: string, value: string | undefined = undefined) => {
    document.execCommand(command, false, value);
    updateContent();
  };

  const updateContent = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleInput = () => {
    updateContent();
  };

  const handleHtmlChange = (html: string) => {
    onChange(html);
  };

  const applyTextColor = (color: string) => {
    execCommand('foreColor', color);
    setSelectedColor(color);
    setShowColorPicker(false);
  };

  const applyBackgroundColor = (color: string) => {
    execCommand('hiliteColor', color);
    setSelectedBgColor(color);
    setShowBgColorPicker(false);
  };

  const insertLink = () => {
    if (!linkUrl) return;
    
    const text = linkText || linkUrl;
    const selection = window.getSelection();
    
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const link = document.createElement('a');
      link.href = linkUrl;
      link.textContent = text;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.className = 'text-blue-600 underline hover:text-blue-800';
      
      range.deleteContents();
      range.insertNode(link);
      
      // Move cursor after link
      range.setStartAfter(link);
      range.setEndAfter(link);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    
    updateContent();
    setLinkUrl('');
    setLinkText('');
    setShowLinkDialog(false);
  };

  const insertImage = () => {
    if (!imageUrl && !imagePreview) return;
    
    // Focus the editor first
    if (editorRef.current) {
      editorRef.current.focus();
    }
    
    const img = document.createElement('img');
    img.src = imagePreview || imageUrl;
    img.alt = imageAlt || 'Image';
    img.className = 'max-w-full h-auto rounded-lg my-2';
    img.style.display = 'block';
    
    if (imageWidth) img.style.width = imageWidth + (imageWidth.includes('%') || imageWidth.includes('px') ? '' : 'px');
    if (imageHeight) img.style.height = imageHeight + (imageHeight.includes('%') || imageHeight.includes('px') ? '' : 'px');
    
    // Insert the image into the editor
    if (editorRef.current) {
      const selection = window.getSelection();
      
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        
        // If the range is not within the editor, create a new range at the end
        if (!editorRef.current.contains(range.commonAncestorContainer)) {
          range.selectNodeContents(editorRef.current);
          range.collapse(false); // Collapse to end
        }
        
        range.deleteContents();
        
        // Add a line break before image if needed
        const br1 = document.createElement('br');
        range.insertNode(br1);
        
        // Insert the image
        range.insertNode(img);
        
        // Add a line break after image for better editing
        const br2 = document.createElement('br');
        range.setStartAfter(img);
        range.insertNode(br2);
        
        // Move cursor after the line break
        range.setStartAfter(br2);
        range.setEndAfter(br2);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        // Fallback: append at the end
        editorRef.current.appendChild(document.createElement('br'));
        editorRef.current.appendChild(img);
        editorRef.current.appendChild(document.createElement('br'));
      }
      
      updateContent();
    }
    
    // Reset dialog
    setImageUrl('');
    setImageAlt('');
    setImageWidth('');
    setImageHeight('');
    setImageFile(null);
    setImagePreview('');
    setShowImageDialog(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }

      setImageFile(file);
      
      // Convert to base64 for preview and insertion
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setImagePreview(result);
        setImageUrl(''); // Clear URL when file is selected
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUrlChange = (url: string) => {
    setImageUrl(url);
    if (url) {
      setImageFile(null);
      setImagePreview('');
    }
  };

  const resetImageDialog = () => {
    setImageUrl('');
    setImageAlt('');
    setImageWidth('');
    setImageHeight('');
    setImageFile(null);
    setImagePreview('');
    setShowImageDialog(false);
  };

  const insertHtmlCode = () => {
    const html = prompt('Enter HTML code:');
    if (html) {
      document.execCommand('insertHTML', false, html);
      updateContent();
    }
  };

  const changeFontSize = (size: string) => {
    execCommand('fontSize', size);
  };

  const changeFormat = (format: string) => {
    execCommand('formatBlock', format);
  };

  const predefinedColors = [
    '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
    '#800000', '#008000', '#000080', '#808000', '#800080', '#008080', '#C0C0C0', '#808080',
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2'
  ];

  const toolbarSections = [
    {
      name: 'History',
      buttons: [
        { icon: Undo, command: 'undo', title: 'Undo (Ctrl+Z)' },
        { icon: Redo, command: 'redo', title: 'Redo (Ctrl+Y)' },
      ]
    },
    {
      name: 'Format',
      buttons: [
        { icon: Bold, command: 'bold', title: 'Bold (Ctrl+B)' },
        { icon: Italic, command: 'italic', title: 'Italic (Ctrl+I)' },
        { icon: Underline, command: 'underline', title: 'Underline (Ctrl+U)' },
        { icon: Strikethrough, command: 'strikeThrough', title: 'Strikethrough' },
      ]
    },
    {
      name: 'Script',
      buttons: [
        { icon: Superscript, command: 'superscript', title: 'Superscript' },
        { icon: Subscript, command: 'subscript', title: 'Subscript' },
      ]
    },
    {
      name: 'Alignment',
      buttons: [
        { icon: AlignLeft, command: 'justifyLeft', title: 'Align Left' },
        { icon: AlignCenter, command: 'justifyCenter', title: 'Align Center' },
        { icon: AlignRight, command: 'justifyRight', title: 'Align Right' },
        { icon: AlignJustify, command: 'justifyFull', title: 'Justify' },
      ]
    },
    {
      name: 'Lists',
      buttons: [
        { icon: List, command: 'insertUnorderedList', title: 'Bullet List' },
        { icon: ListOrdered, command: 'insertOrderedList', title: 'Numbered List' },
      ]
    },
  ];

  return (
    <Card className="overflow-hidden border-2 border-gray-200">
      {/* Toolbar */}
      <div className="bg-gray-50 border-b-2 border-gray-200 p-2">
        <div className="flex flex-wrap gap-2 items-center">
          {/* Format Dropdown */}
          <Select onValueChange={changeFormat}>
            <SelectTrigger className="w-32 h-8">
              <SelectValue placeholder="Format" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="p">Paragraph</SelectItem>
              <SelectItem value="h1">Heading 1</SelectItem>
              <SelectItem value="h2">Heading 2</SelectItem>
              <SelectItem value="h3">Heading 3</SelectItem>
              <SelectItem value="h4">Heading 4</SelectItem>
              <SelectItem value="h5">Heading 5</SelectItem>
              <SelectItem value="h6">Heading 6</SelectItem>
              <SelectItem value="pre">Preformatted</SelectItem>
              <SelectItem value="blockquote">Quote</SelectItem>
            </SelectContent>
          </Select>

          {/* Font Size Dropdown */}
          <Select onValueChange={changeFontSize}>
            <SelectTrigger className="w-24 h-8">
              <SelectValue placeholder="Size" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Very Small</SelectItem>
              <SelectItem value="2">Small</SelectItem>
              <SelectItem value="3">Normal</SelectItem>
              <SelectItem value="4">Medium</SelectItem>
              <SelectItem value="5">Large</SelectItem>
              <SelectItem value="6">Very Large</SelectItem>
              <SelectItem value="7">Huge</SelectItem>
            </SelectContent>
          </Select>

          <div className="w-px h-6 bg-gray-300" />

          {/* Toolbar Buttons */}
          {toolbarSections.map((section, sectionIdx) => (
            <div key={sectionIdx} className="flex gap-1">
              {section.buttons.map((btn, idx) => (
                <Button
                  key={idx}
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => execCommand(btn.command)}
                  title={btn.title}
                  className="h-8 w-8 p-0 hover:bg-gray-200"
                >
                  <btn.icon className="h-4 w-4" />
                </Button>
              ))}
              {sectionIdx < toolbarSections.length - 1 && (
                <div className="w-px h-6 bg-gray-300 mx-1" />
              )}
            </div>
          ))}

          <div className="w-px h-6 bg-gray-300" />

          {/* Color Buttons */}
          <div className="relative">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowColorPicker(!showColorPicker)}
              title="Text Color"
              className="h-8 w-8 p-0 hover:bg-gray-200"
            >
              <div className="relative">
                <Type className="h-4 w-4" />
                <div 
                  className="absolute -bottom-0.5 left-0 right-0 h-1 rounded"
                  style={{ backgroundColor: selectedColor }}
                />
              </div>
            </Button>
            {showColorPicker && (
              <div className="absolute top-full left-0 mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg p-3 z-50">
                <div className="grid grid-cols-8 gap-1 mb-2">
                  {predefinedColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform"
                      style={{ backgroundColor: color }}
                      onClick={() => applyTextColor(color)}
                      title={color}
                    />
                  ))}
                </div>
                <div className="flex gap-2 items-center pt-2 border-t">
                  <input
                    type="color"
                    value={selectedColor}
                    onChange={(e) => applyTextColor(e.target.value)}
                    className="w-8 h-8 rounded cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={selectedColor}
                    onChange={(e) => setSelectedColor(e.target.value)}
                    className="h-8 text-xs"
                    placeholder="#000000"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="relative">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowBgColorPicker(!showBgColorPicker)}
              title="Background Color"
              className="h-8 w-8 p-0 hover:bg-gray-200"
            >
              <Palette className="h-4 w-4" />
            </Button>
            {showBgColorPicker && (
              <div className="absolute top-full left-0 mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg p-3 z-50">
                <div className="grid grid-cols-8 gap-1 mb-2">
                  {predefinedColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform"
                      style={{ backgroundColor: color }}
                      onClick={() => applyBackgroundColor(color)}
                      title={color}
                    />
                  ))}
                </div>
                <div className="flex gap-2 items-center pt-2 border-t">
                  <input
                    type="color"
                    value={selectedBgColor}
                    onChange={(e) => applyBackgroundColor(e.target.value)}
                    className="w-8 h-8 rounded cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={selectedBgColor}
                    onChange={(e) => setSelectedBgColor(e.target.value)}
                    className="h-8 text-xs"
                    placeholder="#ffffff"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="w-px h-6 bg-gray-300" />

          {/* Insert Buttons */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowLinkDialog(true)}
            title="Insert Link"
            className="h-8 w-8 p-0 hover:bg-gray-200"
          >
            <LinkIcon className="h-4 w-4" />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowImageDialog(true)}
            title="Insert Image"
            className="h-8 w-8 p-0 hover:bg-gray-200"
          >
            <ImageIcon className="h-4 w-4" />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={insertHtmlCode}
            title="Insert HTML Code"
            className="h-8 w-8 p-0 hover:bg-gray-200"
          >
            <Code2 className="h-4 w-4" />
          </Button>

          <div className="w-px h-6 bg-gray-300" />

          {/* Clear Formatting */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => execCommand('removeFormat')}
            title="Clear Formatting"
            className="h-8 w-8 p-0 hover:bg-gray-200"
          >
            <RemoveFormatting className="h-4 w-4" />
          </Button>

          {/* HTML Mode Toggle */}
          <div className="ml-auto">
            <Button
              type="button"
              variant={showHtmlMode ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setShowHtmlMode(!showHtmlMode)}
              className="h-8 px-3"
            >
              {showHtmlMode ? <Eye className="h-4 w-4 mr-1" /> : <Code className="h-4 w-4 mr-1" />}
              {showHtmlMode ? 'Visual' : 'HTML'}
            </Button>
          </div>
        </div>
      </div>

      {/* Editor */}
      {showHtmlMode ? (
        <Textarea
          value={value}
          onChange={(e) => handleHtmlChange(e.target.value)}
          className="w-full p-4 font-mono text-sm border-0 focus:outline-none resize-none rounded-none"
          style={{ minHeight }}
          placeholder="Enter HTML code here..."
        />
      ) : (
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          className="w-full p-4 focus:outline-none prose prose-sm max-w-none overflow-auto"
          style={{ minHeight }}
          data-placeholder={placeholder}
          suppressContentEditableWarning
        />
      )}

      {/* Link Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Insert Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="link-text">Link Text</Label>
              <Input
                id="link-text"
                placeholder="Enter text to display (leave empty to use URL)"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="link-url">URL *</Label>
              <Input
                id="link-url"
                placeholder="https://example.com"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkDialog(false)}>
              Cancel
            </Button>
            <Button onClick={insertLink} disabled={!linkUrl}>
              Insert Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Dialog */}
      <Dialog open={showImageDialog} onOpenChange={setShowImageDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Insert Image</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Image Preview */}
            {(imagePreview || imageUrl) && (
              <div className="space-y-2">
                <Label>Preview</Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex items-center justify-center bg-gray-50">
                  <img
                    src={imagePreview || imageUrl}
                    alt="Preview"
                    className="max-h-48 max-w-full object-contain rounded"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle"%3EInvalid Image%3C/text%3E%3C/svg%3E';
                    }}
                  />
                </div>
              </div>
            )}

            {/* File Upload Section */}
            <div className="space-y-2">
              <Label>Upload from Computer</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <ImageIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="mb-2"
                >
                  Choose Image File
                </Button>
                <p className="text-xs text-gray-500">
                  {imageFile ? `Selected: ${imageFile.name}` : 'PNG, JPG, GIF up to 5MB'}
                </p>
              </div>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">OR</span>
              </div>
            </div>

            {/* URL Input Section */}
            <div className="space-y-2">
              <Label htmlFor="image-url">Insert from URL</Label>
              <Input
                id="image-url"
                placeholder="https://example.com/image.jpg"
                value={imageUrl}
                onChange={(e) => handleImageUrlChange(e.target.value)}
              />
              <p className="text-xs text-gray-500">Enter the full URL of the image</p>
            </div>

            {/* Alt Text */}
            <div className="space-y-2">
              <Label htmlFor="image-alt">Alt Text (Recommended)</Label>
              <Input
                id="image-alt"
                placeholder="Description of the image for accessibility"
                value={imageAlt}
                onChange={(e) => setImageAlt(e.target.value)}
              />
            </div>

            {/* Dimensions */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="image-width">Width (optional)</Label>
                <Input
                  id="image-width"
                  placeholder="e.g., 500px or 100%"
                  value={imageWidth}
                  onChange={(e) => setImageWidth(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="image-height">Height (optional)</Label>
                <Input
                  id="image-height"
                  placeholder="e.g., 300px or auto"
                  value={imageHeight}
                  onChange={(e) => setImageHeight(e.target.value)}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetImageDialog}>
              Cancel
            </Button>
            <Button onClick={insertImage} disabled={!imageUrl && !imagePreview}>
              Insert Image
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9CA3AF;
          pointer-events: none;
        }
        [contenteditable]:focus {
          outline: none;
        }
        [contenteditable] img {
          max-width: 100%;
          height: auto;
        }
        [contenteditable] a {
          color: #2563eb;
          text-decoration: underline;
        }
        [contenteditable] a:hover {
          color: #1e40af;
        }
        [contenteditable] blockquote {
          border-left: 4px solid #e5e7eb;
          padding-left: 1rem;
          margin: 1rem 0;
          color: #6b7280;
        }
        [contenteditable] pre {
          background-color: #f3f4f6;
          padding: 1rem;
          border-radius: 0.375rem;
          overflow-x: auto;
          font-family: monospace;
        }
        [contenteditable] h1 {
          font-size: 2rem;
          font-weight: bold;
          margin: 1rem 0;
        }
        [contenteditable] h2 {
          font-size: 1.5rem;
          font-weight: bold;
          margin: 0.875rem 0;
        }
        [contenteditable] h3 {
          font-size: 1.25rem;
          font-weight: bold;
          margin: 0.75rem 0;
        }
        [contenteditable] ul, [contenteditable] ol {
          margin: 1rem 0;
          padding-left: 2rem;
        }
        [contenteditable] li {
          margin: 0.25rem 0;
        }
      `}</style>
    </Card>
  );
}

export default RichTextEditor;